import React, { Component } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { fitRigidRegistration, validateUtmCrs } from './designCrs.js';
import './DesignRegistrationForm.css';

const EMPTY_POINT = () => ({ designE: '', designN: '', designZ: '', mapE: '', mapN: '', mapZ: '' });

const pointRows = (value) => {
    if (!value || !Array.isArray(value.controlPoints)) return [EMPTY_POINT(), EMPTY_POINT(), EMPTY_POINT()];
    const rows = value.controlPoints.map(row => ({
        designE: String(row.design[0]), designN: String(row.design[1]), designZ: String(row.design[2]),
        mapE: String(row.map[0]), mapN: String(row.map[1]), mapZ: String(row.map[2])
    }));
    while (rows.length < 3) rows.push(EMPTY_POINT());
    return rows;
};

class DesignRegistrationForm extends Component {
    constructor(props) {
        super(props);
        const value = props.value || {};
        this.state = {
            sourceZone: value.sourceCrs ? String(value.sourceCrs.zone) : '',
            sourceHemisphere: value.sourceCrs ? value.sourceCrs.hemisphere : '',
            sourceDatum: value.sourceCrs ? value.sourceCrs.datum : '',
            targetZone: value.targetCrs ? String(value.targetCrs.zone) : '',
            targetHemisphere: value.targetCrs ? value.targetCrs.hemisphere : '',
            targetDatum: value.targetCrs ? value.targetCrs.datum : '',
            points: pointRows(value),
            tolerance: value.toleranceMetres === undefined ? '' : String(value.toleranceMetres),
            error: '',
            saving: false
        };
    }

    handleChange = (event) => {
        this.setState({ [event.target.name]: event.target.value, error: '' });
    }

    handlePointChange = (event) => {
        const index = Number(event.target.dataset.pointIndex);
        const key = event.target.dataset.pointField;
        const value = event.target.value;
        this.setState(state => {
            const points = state.points.slice();
            points[index] = { ...points[index], [key]: value };
            return { points, error: '' };
        });
    }

    addPoint = () => this.setState(state => ({ points: state.points.concat([EMPTY_POINT()]) }));

    removePoint = (index) => this.setState(state => ({ points: state.points.filter((point, i) => i !== index) }));

    submit = async (event) => {
        event.preventDefault();
        if (this.state.saving) return;
        try {
            const sourceCrs = validateUtmCrs({ zone: this.state.sourceZone, hemisphere: this.state.sourceHemisphere, datum: this.state.sourceDatum });
            const targetCrs = validateUtmCrs({ zone: this.state.targetZone, hemisphere: this.state.targetHemisphere, datum: this.state.targetDatum });
            const toleranceMetres = Number(this.state.tolerance);
            if (!Number.isFinite(toleranceMetres) || toleranceMetres <= 0)
                throw new Error('Enter the survey-approved registration tolerance in metres');

            const controls = this.state.points.map((row, index) => {
                const values = ['designE', 'designN', 'designZ', 'mapE', 'mapN', 'mapZ'].map(key => Number(row[key]));
                if (!values.every(value => Number.isFinite(value)) || Object.values(row).some(value => value === ''))
                    throw new Error('Enter all six coordinates for control point ' + (index + 1));
                return { design: values.slice(0, 3), map: values.slice(3, 6) };
            });

            // The surveyed pairs directly define the source-to-recording rigid
            // transform. UTM metadata is retained for provenance; no datum shift
            // or scale is guessed, and the residual check rejects incompatible
            // grids or bad control points.
            const registration = fitRigidRegistration(controls.map(point => point.design), controls.map(point => point.map));
            if (registration.rms > toleranceMetres)
                throw new Error('Control-point RMS ' + registration.rms.toFixed(3) + ' m exceeds the approved tolerance of ' + toleranceMetres + ' m');

            if (typeof this.props.onSave !== 'function') throw new Error('Registration saving is unavailable');
            this.setState({ saving: true, error: '' });
            await this.props.onSave({
                version: 1,
                sourceCrs,
                targetCrs,
                controlPoints: controls,
                toleranceMetres,
                registration,
                registered: true
            });
            this.setState({ saving: false });
        } catch (error) {
            this.setState({ saving: false, error: error.message || 'Could not calculate registration' });
        }
    }

    render() {
        const crsFields = (prefix, label) => (
            <fieldset className="design-registration-crs">
                <legend>{label}</legend>
                <label>{label} UTM zone
                    <input aria-label={label + ' UTM zone'} name={prefix + 'Zone'} inputMode="numeric" value={this.state[prefix + 'Zone']} onChange={this.handleChange} />
                </label>
                <label>{label} hemisphere
                    <select aria-label={label + ' hemisphere'} name={prefix + 'Hemisphere'} value={this.state[prefix + 'Hemisphere']} onChange={this.handleChange}>
                        <option value="">Select</option><option value="N">North</option><option value="S">South</option>
                    </select>
                </label>
                <label>{label} datum
                    <select aria-label={label + ' datum'} name={prefix + 'Datum'} value={this.state[prefix + 'Datum']} onChange={this.handleChange}>
                        <option value="">Select</option>
                        <option value="WGS84">WGS84</option><option value="GDA94">GDA94</option>
                        <option value="GDA2020">GDA2020</option><option value="GRS80">GRS80</option>
                    </select>
                </label>
            </fieldset>
        );

        return (
            <form className="design-registration" onSubmit={this.submit}>
                <h3>Register design coordinates</h3>
                <p>Choose each coordinate set’s UTM zone, hemisphere and datum. Enter three or more surveyed points present in both sets. The ties define translation and rotation; no datum shift or scale is guessed. An approved 3D RMS tolerance is required.</p>
                <div className="design-registration-crs-pair">
                    {crsFields('source', 'Design')}
                    {crsFields('target', 'Recording')}
                </div>
                <label className="design-registration-tolerance">Acciona-approved maximum 3D RMS error (m)
                    <input aria-label="Acciona-approved maximum 3D RMS error (m)" name="tolerance" type="number" min="0.001" step="0.001" value={this.state.tolerance} onChange={this.handleChange} />
                </label>
                <div className="design-registration-points-scroll">
                    <table className="design-registration-points">
                        <thead><tr><th>Point</th><th>Design E</th><th>Design N</th><th>Design Z</th><th>Recording E</th><th>Recording N</th><th>Recording Z</th></tr></thead>
                        <tbody>{this.state.points.map((point, index) => (
                            <tr key={index}>
                                <th scope="row">{index + 1}{index >= 3 && <button type="button" aria-label={'Remove point ' + (index + 1)} onClick={() => this.removePoint(index)}>×</button>}</th>
                                {['designE', 'designN', 'designZ', 'mapE', 'mapN', 'mapZ'].map(key => (
                                    <td key={key}><input aria-label={'Point ' + (index + 1) + ' ' + key} type="number" step="any" value={point[key]} data-point-index={index} data-point-field={key} onChange={this.handlePointChange} /></td>
                                ))}
                            </tr>
                        ))}</tbody>
                    </table>
                </div>
                <Button type="button" variant="outline-secondary" onClick={this.addPoint}>Add control point</Button>
                {this.state.error && <Alert variant="danger" role="alert">{this.state.error}</Alert>}
                <div className="design-registration-actions">
                    <Button type="button" variant="secondary" onClick={this.props.onCancel}>Cancel</Button>
                    <Button type="submit" variant="primary" disabled={this.state.saving}>{this.state.saving ? 'Saving…' : 'Validate and save'}</Button>
                </div>
            </form>
        );
    }
}

export default DesignRegistrationForm;
